import React, { useState, useEffect, useMemo } from "react";
import { OptionProps } from "react-select";
import Select from "react-select/creatable";

// Type definitions
interface Contact {
  id: string;
  fullName?: string;
  name?: string;
  organizationName?: string;
  emails?: Array<{
    // Post-Phase-5 shape: `email` is the raw-string field on each entry.
    email?: string;
    is_primary?: boolean;
  }>;
}

interface ContactOption {
  value: string;
  label: string;
  contact?: Contact;
  searchTerm?: string;
  isContact?: boolean;
  isCreateOption?: boolean;
}

interface CreateContactData {
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  region: string;
  zip: string;
  country: string;
  contactID: string;
}

interface ContactMultiSelectDropdownProps {
  contacts?: Contact[];
  isLoadingContacts?: boolean;
  searchValue?: string;
  onSearchChange: (value: string) => void;
  onContactSelect: (contact: Contact) => void;
  onCreateContact: (contactData: CreateContactData) => void;
  placeholder?: string;
  isInvalid?: boolean;
  hasSearched?: boolean; // Add this prop to track if search has completed
}

interface CustomStyles {
  control: (provided: any, state: any) => any;
  valueContainer: (provided: any) => any;
  menu: (provided: any) => any;
  menuPortal: (provided: any) => any;
  option: (provided: any, state: any) => any;
  singleValue: (provided: any) => any;
  input: (provided: any) => any;
  placeholder: (provided: any) => any;
}

const customStyles: CustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? "1px solid #6B8E6B" : "1px solid #E0D9CC",
    boxShadow: "none",
    "&:hover": {
      border: "1px solid #6B8E6B",
      boxShadow: "none",
    },
    cursor: "default",
    minHeight: "32px",
    height: "32px",
    borderRadius: "6px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "30px",
    padding: "0 10px",
    alignItems: "center",
    overflow: "visible",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 50,
    marginTop: "4px",
    borderRadius: "6px",
    border: "1px solid #E0D9CC",
    backgroundColor: '#FCFBF8',
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 100,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#F0ECE4" : '#FCFBF8',
    color: state.isFocused ? "#3A3429" : "#6B6458",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#F0ECE4",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#6B6458",
    lineHeight: "20px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#6B6458",
    fontSize: "14px",
    lineHeight: "20px",
    height: "20px",
    margin: "0 2px",
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9A9285",
    fontSize: "14px",
    lineHeight: "20px",
    margin: "0 2px 0 8px",
    padding: 0,
  }),
};

// Custom Option component to display contact details
const ContactOption: React.FC<OptionProps<ContactOption, false>> = ({
  data,
  innerProps,
}) => {
  if (data.isCreateOption) {
    return (
      <div
        {...innerProps}
        className="px-3 py-2 text-sm text-green-600 hover:bg-paper-inset focus:bg-paper-inset focus:outline-none"
      >
        <div className="text-sm">Create: {data.label}</div>
      </div>
    );
  }

  // Find the matching email that was searched for
  const searchTerm = data.searchTerm?.toLowerCase();
  const matchingEmail = data.contact?.emails?.find((email) =>
    email.email?.toLowerCase().includes(searchTerm || "")
  )?.email;

  // Fallback to primary email if no matching email found
  const displayEmail =
    matchingEmail ||
    data.contact?.emails?.find((email) => email.is_primary)?.email ||
    data.contact?.emails?.[0]?.email ||
    "";

  return (
    <div
      {...innerProps}
      className="w-full px-3 py-2 text-left text-sm hover:bg-paper-inset focus:bg-paper-inset focus:outline-none"
    >
      <div className="font-medium text-ink">
        {data.contact?.fullName || data.contact?.name || "Unknown Name"}
      </div>
      <div className="text-ink-muted">{displayEmail}</div>
      {data.contact?.organizationName && (
        <div className="text-xs text-ink-muted">
          {data.contact.organizationName}
        </div>
      )}
    </div>
  );
};

const ContactMultiSelectDropdown: React.FC<ContactMultiSelectDropdownProps> = ({
  contacts = [],
  isLoadingContacts = false,
  searchValue = "",
  onSearchChange,
  onContactSelect,
  onCreateContact,
  placeholder = "Search for contact",
  isInvalid = false,
  hasSearched = false, // Add this prop
}) => {
  const [inputValue, setInputValue] = useState<string>(searchValue);

  // Update input value when searchValue prop changes
  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  // Generate options based on search input and contacts
  const options = useMemo((): ContactOption[] => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    // Don't show any options while loading
    if (isLoadingContacts) {
      return [];
    }

    const searchTerm = inputValue.toLowerCase();
    const filteredContacts = contacts.filter((contact) => {
      const contactName = contact.fullName || contact.name || "";

      // Check if any email in the emails array matches
      const emailMatch =
        contact.emails?.some((email) =>
          email.email?.toLowerCase().includes(searchTerm)
        ) || false;

      // Check if name matches
      const nameMatch = contactName.toLowerCase().includes(searchTerm);

      return emailMatch || nameMatch;
    });

    const contactOptions: ContactOption[] = filteredContacts.map((contact) => ({
      value: contact.id,
      label: contact.fullName || contact.name || "",
      contact: contact,
      searchTerm: searchTerm,
      isContact: true,
    }));

    // Only add create option if we've searched and no exact matches found
    if (hasSearched) {
      const hasExactMatch = filteredContacts.some((contact) => {
        const contactName = contact.fullName || contact.name || "";
        return contactName.toLowerCase() === searchTerm;
      });

      if (!hasExactMatch && inputValue.trim()) {
        // Split the input value to check for first and last name
        const nameParts = inputValue.trim().split(/\s+/);
        if (nameParts.length <= 2) {
          contactOptions.push({
            value: `create-${inputValue}`,
            label: inputValue,
            isCreateOption: true,
            searchTerm: searchTerm,
          });
        }
      }
    }

    return contactOptions;
  }, [contacts, inputValue, isLoadingContacts, hasSearched]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onSearchChange(newValue);
  };

  const handleChange = (
    selectedOption: ContactOption | null,
    actionMeta: any
  ) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.isCreateOption) {
      // Handle creating new contact
      const nameParts = selectedOption.label
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      let firstName = "";
      let lastName = "";

      if (nameParts.length === 0) {
        firstName = "Unknown";
        lastName = "Unknown";
      } else if (nameParts.length === 1) {
        firstName = nameParts[0];
        lastName = "Unknown";
      } else {
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(" ");
      }

      onCreateContact({
        firstName,
        lastName,
        email: "",
        organizationName: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        region: "",
        zip: "",
        country: "",
        contactID: crypto.randomUUID(),
      });
    } else {
      // Handle selecting existing contact
      if (selectedOption.contact) {
        onContactSelect(selectedOption.contact);
      }
    }

    // Clear the input after selection
    setInputValue("");
    onSearchChange("");
  };

  const handleMenuOpen = () => {
    // Menu is opening, ensure we have the latest search value
    setInputValue(searchValue);
  };

  return (
    <div className="relative">
      <Select<ContactOption>
        isClearable
        isSearchable
        placeholder={placeholder}
        value={null}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        options={options}
        styles={{
          ...customStyles,
          control: (provided, state) => ({
            ...customStyles.control(provided, state),
            border: isInvalid
              ? "1px solid #ef4444"
              : customStyles.control(provided, state).border,
            minHeight: "32px",
            height: "32px",
          }),
        }}
        components={{
          Option: ContactOption,
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
        }}
        noOptionsMessage={() =>
          isLoadingContacts
            ? "Loading contacts..."
            : inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No matching contacts found"
        }
        loadingMessage={() => "Loading contacts..."}
        isLoading={isLoadingContacts}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
        menuIsOpen={
          inputValue.length >= 2 && (options.length > 0 || isLoadingContacts)
        }
        blurInputOnSelect={true}
        closeMenuOnSelect={true}
        closeMenuOnScroll={false}
        className="text-sm"
      />
    </div>
  );
};

export default ContactMultiSelectDropdown;
